import { finalize, map, catchError, tap } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DataService } from './../../../core/service/base/data.service';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { Observable } from 'rxjs';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Email } from '../../model/email';
import { EmailFilter } from '../../model/email-filter';
import { PagedFilter } from '../../../core/model/paged-filter';

@Injectable()
export class EmailService extends DataService<Email> {
  private readonly _defaultFilterFields: string[] = ['createdDateTime', 'recipientEmailAddress', 'subject'];

  constructor(
    httpClient: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(httpClient, config, messageService, Email, progressBar, 'email');
  }

  createEmailLinkedToAgreements(data: any): Observable<any> {
    return this.client.post(`${this.endpoint}/create/lease-agreement-email`, JSON.stringify(data));
  }

  getFilterDefinition(): Observable<PagedFilter<EmailFilter>> {
    return this.client.get<PagedFilter<EmailFilter>>(`${this.endpoint}/filter`).pipe(
      map(res => {
        let result = res;
        result.filter.defaultFields = this._defaultFilterFields;
        if (!result.filter.selectedFields || result.filter.selectedFields.length === 0) {
          result.filter.selectedFields = this._defaultFilterFields;
        }

        return result;
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  send(id: string): Observable<any> {
    return this.client
      .put(`${this.endpoint}/send`, {}, { params: new HttpParams().append('id', id) })
      .pipe(tap(() => this.notification.showSuccess('SUCCESS.LETTER_SENT')));
  }

  sendNew(email: any): Observable<any> {
    return this.client.put(`${this.endpoint}/send`, JSON.stringify(email));
  }

  change(model: Email): Observable<any> {
    this.progressBar.start();
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model));
    for (let file of model.attachments) {
      formData.append('attachments[]', file, file.name);
    }
    return this.client.put(this.endpoint, formData).pipe(
      tap(() => {
        this.notification.showSuccess();
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  sendEmails(email: { recipientEmailAddresses: string[]; body: string; subject: string; attachments: any[] }): Observable<any> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(email));
    for (let file of email.attachments) {
      formData.append('attachments[]', file, file.name);
    }
    return this.client.post(`${this.endpoint}/send-generic`, formData);
  }

  sendEmailToTenants(email: any): Observable<any> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(email));
    for (let file of email.attachments) {
      formData.append('attachments[]', file, file.name);
    }
    return this.client.post(`${this.endpoint}/send/tenant-email`, formData);
  }

  downloadAttachment(attachmentId: string): Observable<File> {
    this.progressBar.start();
    return this.getFile(`${this.endpoint}/attachment-file/${attachmentId}`, 'document').pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }
}
